import { useCallback } from 'react'
import { useActiveAccounts } from '@data/accounts/accounts'
import { useLocationsQuery } from '@data/locations/locations'
import CustomerLocation from '@data/locations/model/customerLocation'
import { useAccountStore } from '@store/account.store'
import { useLocationStore } from '@store/location.store'

const EMPTY_LOCATIONS: CustomerLocation[] = []

// Changing the account selection, and reconciling the location selection that depends on it,
// as one operation (STATE-2).
//
// This replaced `useLocationSelectionReconciler`, an effect that recovered the event
// "account selection changed from X to Y" out of state, by diffing a ref. Everything
// awkward about that followed from the reconstruction: a ref for the previous value, a
// join-string key to dodge array-identity churn, an imperative store read to avoid
// re-running on its own write, and a mount-order dependency in AppDataBootstrap. The event
// has exactly one producer — the account picker — so the action can simply be called there,
// holding all four inputs the decision needs.
//
// It lives in neither domain module on purpose. It writes the account store and the location
// store and reads the locations query, and `@data/accounts` cannot import `@data/locations`
// without a cycle, since locations already reads the accounts query for its loading state.
//
// The trade this makes, stated plainly: an effect reacted to *any* account-selection change
// however it was caused, while an action only reconciles when someone calls it. Every
// account-selection change goes through here — since STATE-1 deleted the bootstrap effect,
// there is no longer any legitimate caller of the raw store setter outside `reset`.
export const useAccountSelection = () => {
  const { data } = useLocationsQuery()
  const allLocations = data ?? EMPTY_LOCATIONS
  const activeAccounts = useActiveAccounts()
  const setSelectedAccountIds = useAccountStore((state) => state.setSelectedAccountIds)
  const setSelectedLocationIds = useLocationStore((state) => state.setSelectedLocationIds)

  return useCallback(
    (nextAccountIds: string[]) => {
      // Read at call time, not at render: the action needs the values as they are when the
      // user acts, and reading them here keeps this out of the dependency array.
      const storedAccountIds = useAccountStore.getState().selectedAccountIds
      const selectedLocationIds = useLocationStore.getState().selectedLocationIds
      const activeAccountIds = activeAccounts.map((account) => account.id)

      // Resolve the default before diffing, or every id looks newly added on the first
      // change away from it.
      const previousAccountIds = storedAccountIds ?? activeAccountIds

      const nextAccountIdSet = new Set(nextAccountIds)

      // Selecting every account stores the default rather than a snapshot of it.
      //
      // Without this the picker's "select all" is a one-way door: it writes an explicit
      // list of the ids that exist right now, which looks identical to the default in the
      // UI but silently stops including accounts created later, and nothing in the
      // interface can get back to `null`. The location selector has the same rule for the
      // same reason.
      const isDefaultSelection =
        activeAccountIds.length > 0 &&
        nextAccountIds.length === activeAccountIds.length &&
        activeAccountIds.every((id) => nextAccountIdSet.has(id))

      setSelectedAccountIds(isDefaultSelection ? null : nextAccountIds)

      // `null` is "all visible locations" and is derived rather than stored, so it cannot go
      // stale and there is nothing to reconcile.
      if (selectedLocationIds === null) return

      const previousAccountIdSet = new Set(previousAccountIds)

      // Locations whose account is still selected. An id with no matching location is kept
      // rather than dropped: the list may not have loaded, and a missing location is not
      // evidence that its account was deselected.
      const kept = selectedLocationIds.filter((locationId) => {
        const location = allLocations.find((item) => item.id === locationId)

        return location === undefined || nextAccountIdSet.has(location.accountIdRef ?? '')
      })

      // Active locations belonging to accounts that were just added. Accounts that were
      // already selected contribute nothing, so a user's existing choices survive.
      const added = allLocations
        .filter(
          (location) =>
            location.active &&
            !previousAccountIdSet.has(location.accountIdRef ?? '') &&
            nextAccountIdSet.has(location.accountIdRef ?? ''),
        )
        .map((location) => location.id ?? '')

      const nextLocationIds = [...kept, ...added]

      if (
        nextLocationIds.length !== selectedLocationIds.length ||
        nextLocationIds.some((locationId, index) => locationId !== selectedLocationIds[index])
      ) {
        setSelectedLocationIds(nextLocationIds)
      }
    },
    [allLocations, activeAccounts, setSelectedAccountIds, setSelectedLocationIds],
  )
}
